import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only redirect if not already on login page to prevent loops
      if (window.location.pathname !== '/login') {
        localStorage.removeItem('token');
        // Use replace to prevent adding to history
        window.location.replace('/login');
      }
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  login: (credentials: { email: string; password: string }) =>
    api.post('/auth/login', credentials),
  register: (userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    role?: string;
  }) => api.post('/auth/register', userData),
  getCurrentUser: () => api.get('/users/me'),
};

export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
};

export const roomsApi = {
  getAll: () => api.get('/rooms'),
  getById: (id: number) => api.get(`/rooms/${id}`),
  create: (data: any) => api.post('/rooms', data),
  update: (id: number, data: any) => api.put(`/rooms/${id}`, data),
  delete: (id: number) => api.delete(`/rooms/${id}`),
  updateStatus: (id: number, status: string) => api.patch(`/rooms/${id}/status`, { status }),
};

export const guestsApi = {
  getAll: () => api.get('/guests'),
  getById: (id: number) => api.get(`/guests/${id}`),
  create: (data: any) => api.post('/guests', data),
  update: (id: number, data: any) => api.put(`/guests/${id}`, data),
  delete: (id: number) => api.delete(`/guests/${id}`),
  search: (query: string) => api.get(`/guests/search?q=${query}`),
};

export const roomTypesApi = {
  getAll: () => api.get('/room-types'),
  getById: (id: number) => api.get(`/room-types/${id}`),
  create: (data: any) => api.post('/room-types', data),
  update: (id: number, data: any) => api.put(`/room-types/${id}`, data),
  delete: (id: number) => api.delete(`/room-types/${id}`),
};

export const reservationsApi = {
  getAll: (params?: any) => api.get('/reservations', { params }),
  getById: (id: number) => api.get(`/reservations/${id}`),
  create: (data: any) => api.post('/reservations', data),
  update: (id: number, data: any) => api.put(`/reservations/${id}`, data),
  checkAvailability: (params: any) => api.get('/reservations/availability/check', { params }),
  checkIn: (id: number, data: any) => api.post(`/reservations/${id}/checkin`, data),
  checkOut: (id: number, data: any) => api.post(`/reservations/${id}/checkout`, data),
  cancel: (id: number) => api.post(`/reservations/${id}/cancel`),
};

export const inventoryApi = {
  getAll: (params?: any) => api.get('/inventory', { params }),
  getById: (id: number) => api.get(`/inventory/${id}`),
  create: (data: any) => api.post('/inventory', data),
  update: (id: number, data: any) => api.put(`/inventory/${id}`, data),
  delete: (id: number) => api.delete(`/inventory/${id}`),
};

export const financialApi = {
  getTransactions: (params?: any) => api.get('/financial/transactions', { params }),
  getSummary: (params?: any) => api.get('/financial/summary', { params }),
  getRevenue: (params?: any) => api.get('/financial/revenue', { params }),
  createTransaction: (data: any) => api.post('/financial/transactions', data),
};

export const employeesApi = {
  getAll: () => api.get('/employees'),
  getById: (id: number) => api.get(`/employees/${id}`),
  update: (id: number, data: any) => api.put(`/employees/${id}`, data),
  delete: (id: number) => api.delete(`/employees/${id}`),
};

export default api;

